import { FC, useState } from 'react';
import { useModal } from 'store/modal';
import API from 'core/Api';
import { FaTimes } from 'react-icons/fa';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { useTranslation } from 'react-i18next';
import { createErrorFromResponse } from 'core/Error';
import { useForm } from 'react-hook-form';
import Form from 'components/form/Form';
import FormGroup from 'components/form/FormGroup';
import handleEmployeeCode from 'utils/helpers/handleEmployeeCode';
import { HandleEmployeeSignOptions } from 'components/form/CodeForm';
import { IRejectDocument, IPropsReasonForm } from '../types';

interface IProps {
  leaveId: number;
  buttonText: string;
  isMobile?: boolean;
  invalidate: () => void;
  isWithoutDocument?: boolean;
}

export interface IInputsRejectLeave {
  reason: string;
}

interface ISignButtonProps {
  rejectDocument: IRejectDocument;
  invalidate: any;
}

const ReasonForm: FC<IPropsReasonForm> = ({ generateRejectDocument, leaveId, isWithoutDocument }: IPropsReasonForm) => {
  const methods = useForm<IInputsRejectLeave>();
  const { t } = useTranslation();

  return (
    <>
      <p className="text-center">{t('documents.table.confirmRejectReasonLabel')}*</p>
      <Form
        methods={methods}
        onSubmit={(data: IInputsRejectLeave) => generateRejectDocument(leaveId, data.reason)}
        btnLabel={isWithoutDocument ? t('leaves.rejectLeave') : t('leaves.generateRejectRequestDocument')}
        primaryActionBtnType="primary"
        hideCancelBtn
      >
        <FormGroup
          controlId="formGroupReason"
          isRequired
          as="textarea"
          name="reason"
        />
      </Form>
    </>
  );
};

const SignButton: FC<ISignButtonProps> = ({ rejectDocument, invalidate }: ISignButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const { showModal } = useModal();

  // Sign document as employee
  const handleEmployeeLeaveSign = (options: HandleEmployeeSignOptions) => {
    // Sign process used in "employee" mode.
    // AdES (Advanced Electronic Signature)
    //
    // The process takes place with one request to BE,
    // as the signing is done with self-signed certificates,
    // which are kept on the backend.
    const { docId, data } = options;
    return API.post(`documents/${docId}/sign-ades`, data)
      .then(() => {
        showModal({
          type: 'success',
          content: <>{t('leaves.rejectRequestSuccessMsg')}.</>,
        });

        invalidate();
      })
      .catch(response => {
        const error = createErrorFromResponse(response);
        showModal({
          type: 'error',
          content: createErrorFromResponse(error).message,
        });
      });
  };

  return (
    <Button
      type="button"
      btnType="danger"
      preventDebounce
      loading={isLoading}
      onClick={() => {
        setIsLoading(true);

        if (isLoading) {
          return;
        }

        handleEmployeeCode({
          docId: rejectDocument.id.toString(),
          showModal,
          heading: t('documents.table.signWithCodeLabel'),
          handleEmployeeSign: handleEmployeeLeaveSign,
        });

        invalidate();
      }}
    >
      {t('leaves.signRejectRequestDocument')}
    </Button>
  );
};

const EmployeeRejectLeave: FC<IProps> = ({
  leaveId,
  buttonText,
  invalidate,
  isMobile = false,
  isWithoutDocument = false,
}: IProps) => {
  const { t } = useTranslation();
  const { showModal } = useModal();
  const methods = useForm<IInputsRejectLeave>();
  const [isLoading, setIsLoading] = useState(false);

  const confirmRejectModal = () => {
    // Generate document for rejecetion
    const generateRejectDocument = (id: number, reason: string) =>
      API.post(`leaves/${leaveId}/employee-reject`, {
        reason,
      })
        .then(data => {
          const document = data.data;

          if (isWithoutDocument) {
            showModal({
              type: 'success',
              content: <>{t('leaves.leaveRejectedSuccessMsg')}</>,
            });

            invalidate();
          } else {
            showModal({
              type: 'form',
              heading: t('documents.table.confirmDocHeading'),
              content: (
                <>
                  <p>{t('leaves.employee.confirmRejectConfirmedLeaveMsg')}</p>

                  <Button
                    type="button"
                    btnType="primary"
                    outlined
                    disabled={isLoading}
                    onClick={() => {
                      handlePreviewDoc({ showModal, id: document.id, setIsLoading });
                    }}
                  >
                    {t('documents.table.previewDoc')}
                  </Button>

                  <br />

                  <h4 style={{ marginTop: '15px' }}>{t('leaves.step')} 2:</h4>
                  <hr />

                  <SignButton
                    rejectDocument={document}
                    invalidate={invalidate}
                  />
                </>
              ),
            });
          }
        })
        .catch(err =>
          showModal({
            type: 'error',
            content: createErrorFromResponse(err).message,
          })
        );

    showModal({
      type: 'form',
      heading: t('documents.table.confirmDocHeading'),
      content: (
        <>
          <p>{t('leaves.employer.confirmRejectLeaveMsg')}</p>
          {!isWithoutDocument && <h4>{t('leaves.step')} 1:</h4>}
          <hr />

          <ReasonForm
            generateRejectDocument={generateRejectDocument}
            leaveId={leaveId}
            isWithoutDocument={isWithoutDocument}
          />
        </>
      ),
    });
  };

  return !isMobile ? (
    <div className="d-inline-block">
      <TooltipWrapper
        id="tooltip-reject"
        overlay={buttonText}
      >
        <Button
          type="button"
          btnType="danger"
          size="sm"
          onClick={() => confirmRejectModal()}
        >
          <FaTimes size={14} />
        </Button>
      </TooltipWrapper>
    </div>
  ) : (
    <Button
      type="button"
      btnType="danger"
      onClick={() => confirmRejectModal()}
    >
      {buttonText}
    </Button>
  );
};

export default EmployeeRejectLeave;
